import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, Renderer2, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { State } from '../shared/core/state';
import { Store } from '@ngrx/store';
import { selectIsLoggedIn } from '../shared/core/state/users/user.selectors';
import { TenantService, TenantInfo, TenantLandingPageSettings } from '../shared/services/tenant.service';
import { Subscription } from 'rxjs';
import { HeroSectionComponent } from './components/hero-section/hero-section.component';
import { FeaturesSectionComponent } from './components/features-section/features-section.component';
import { CoursesSectionComponent } from './components/courses-section/courses-section.component';
import { HowItWorksSectionComponent } from './components/how-it-works-section/how-it-works-section.component';
import { VisionSectionComponent } from './components/vision-section/vision-section.component';
import { FaqSectionComponent } from './components/faq-section/faq-section.component';
import { FinalCtaSectionComponent } from './components/final-cta-section/final-cta-section.component';
import { InstructorSectionComponent } from './components/instructor-section/instructor-section.component';
import { LiveClassesSectionComponent } from './components/live-classes-section/live-classes-section.component';
import { VideoDemoSectionComponent } from './components/video-demo-section/video-demo-section.component';
import { TestimonialsSectionComponent } from './components/testimonials-section/testimonials-section.component';
import { PerksSectionComponent } from './components/perks-section/perks-section.component';
import { WhatsappWidgetComponent } from './components/whatsapp-widget/whatsapp-widget.component';

// Type declaration for AOS library
declare global {
  interface Window {
    AOS?: {
      init: (options?: {
        duration?: number;
        easing?: string;
        once?: boolean;
        offset?: number;
      }) => void;
    };
  }
}

@Component({
  selector: 'black-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    HeroSectionComponent,
    LiveClassesSectionComponent,
    FeaturesSectionComponent,
    CoursesSectionComponent,
    HowItWorksSectionComponent,
    VisionSectionComponent,
    FaqSectionComponent,
    FinalCtaSectionComponent,
    InstructorSectionComponent,
    VideoDemoSectionComponent,
    TestimonialsSectionComponent,
    PerksSectionComponent,
    WhatsappWidgetComponent,
  ],
})
export class IndexComponent implements OnInit, OnDestroy, AfterViewInit {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private store = inject<Store<State>>(Store);
  public tenantService = inject(TenantService);

  private sub = new Subscription();
  private scrollListener?: () => void;

  isUserLoggedIn = false;
  currentTenant: TenantInfo | null = null;

  get isDefaultTenant(): boolean {
    return this.tenantService.isDefaultTenant();
  }

  get landingSettings(): TenantLandingPageSettings | undefined {
    return this.currentTenant?.settings?.landingPage;
  }

  get landingTheme(): 'modern' | 'warm' | 'bold' {
    return this.landingSettings?.theme || 'modern';
  }

  get showFeatures(): boolean {
    return this.isDefaultTenant && this.landingSettings?.showFeatures !== false;
  }

  get showCourses(): boolean {
    return this.isDefaultTenant && this.landingSettings?.showCourses !== false;
  }

  get showCustomContent(): boolean {
    return (
      Boolean(this.landingSettings?.customContent?.enabled) &&
      Boolean(this.landingSettings?.customContent?.html)
    );
  }

  get customContentTitle(): string | undefined {
    return this.landingSettings?.customContent?.title;
  }

  get customContentHtml(): string {
    return this.landingSettings?.customContent?.html || '';
  }

  get showFaq(): boolean {
    return this.landingSettings?.showFaq !== false;
  }

  get showCta(): boolean {
    return this.isDefaultTenant && this.landingSettings?.showCta !== false;
  }

  get showInstructor(): boolean {
    return Boolean(this.landingSettings?.instructor?.enabled);
  }

  get showVideoDemo(): boolean {
    return Boolean(this.landingSettings?.videoDemo?.enabled);
  }

  get showTestimonials(): boolean {
    return Boolean(this.landingSettings?.testimonials?.enabled);
  }

  get showPerks(): boolean {
    return Boolean(this.landingSettings?.perks?.enabled);
  }

  get showWhatsApp(): boolean {
    return Boolean(this.landingSettings?.whatsApp?.enabled);
  }

  get showLiveClasses(): boolean {
    return this.landingSettings?.liveClasses?.enabled !== false;
  }

  get instructorAvatar(): string | undefined {
    return this.landingSettings?.instructor?.avatarUrl;
  }

  get instructorName(): string | undefined {
    return this.landingSettings?.instructor?.name;
  }

  get instructorTitle(): string | undefined {
    return this.landingSettings?.instructor?.title;
  }

  get tenantSupportEmail(): string | undefined {
    return (
      this.currentTenant?.settings?.legal?.contactEmail ||
      (this.isDefaultTenant ? 'support@learnbird.lk' : undefined)
    );
  }

  ngOnInit(): void {
    this.initializeScrollAnimations();

    this.sub.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant = tenant;
      })
    );

    // Subscribe to user login state
    this.sub.add(
      this.store.select(selectIsLoggedIn).subscribe((loggedIn) => {
        this.isUserLoggedIn = loggedIn;
      })
    );
  }

  ngAfterViewInit(): void {
    this.initializeIntersectionObserver();
    this.addScrollIndicator();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }

  private initializeScrollAnimations(): void {
    // Add AOS (Animate On Scroll) initialization if available
    if (typeof window !== 'undefined' && window.AOS) {
      window.AOS.init({
        duration: 800,
        easing: 'ease-out-cubic',
        once: true,
        offset: 50,
      });
    }
  }

  private initializeIntersectionObserver(): void {
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const options = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.renderer.addClass(entry.target, 'animate-in');
          }
        });
      }, options);

      // Observe all animated elements
      const animatedElements =
        this.elementRef.nativeElement.querySelectorAll('[data-aos]');
      animatedElements.forEach((el: Element) => observer.observe(el));
    }
  }

  private addScrollIndicator(): void {
    if (typeof window !== 'undefined') {
      this.scrollListener = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrollProgress = (scrollTop / scrollHeight) * 100;

        // Update scroll indicator if it exists
        const indicator = document.querySelector(
          '.scroll-indicator'
        ) as HTMLElement;
        if (indicator) {
          indicator.style.transform = `scaleX(${scrollProgress / 100})`;
        }
      };

      window.addEventListener('scroll', this.scrollListener, { passive: true });
    }
  }

  // Smooth scroll to section
  scrollToSection(sectionId: string): void {
    if (typeof window !== 'undefined') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  // Track user interactions for analytics
  trackButtonClick(buttonName: string): void {
    // Add analytics tracking here if needed
    console.log(`Button clicked: ${buttonName}`);
  }

  // Handle keyboard navigation for accessibility
  onKeyDown(event: KeyboardEvent, action: () => void): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  }
}
